// Slice 2 Checkpoint 9 · Phase 1 — Admin read-fetch consolidation (A1).
//
// Single source of truth for the admin SPA's read-path posture:
// every `fetch*` helper in `./worker.ts` that pulls a JSON artifact
// from the staging Worker delegates to `doAdminReadFetch<T>` below.
//
// Behavior preserved byte-identically from CP1–CP8 worker.ts:
//   - Missing `VITE_WORKER_URL` → `{kind:'error', message:'VITE_WORKER_URL not configured'}`
//     (no `status` field).
//   - Trailing slash on `WORKER_URL` is stripped before concat.
//   - Request options: `GET`, `credentials:'include'`,
//     `headers:{accept:'application/json'}`.
//   - `!res.ok` → `{kind:'error', message:'<label> fetch failed: <res.statusText>', status:<code>}`.
//   - Network throw → `{kind:'error', message: err.message OR 'unknown network error'}`
//     (no `status`).
//   - Empty-vs-ok discrimination via caller-supplied `isEmpty(artifact)`
//     predicate. Both branches carry `artifact` unchanged.
//
// The four current labels (must match the prior call-site strings
// verbatim so any caller relying on the exact error text is not
// surprised):
//   - 'entities.json'
//   - 'recap.json'
//   - 'wiki/admin/list.json'
//   - 'recap-events admin list'
//
// Grounding: FISHSTANK_V2_CHECKPOINT_9_FIRST_SLICE.md §
//   "Signature contract for adminFetch.ts".

export type FetchResult<T> =
  | { kind: 'ok'; artifact: T }
  | { kind: 'empty'; artifact: T }
  | { kind: 'error'; message: string; status?: number }

export interface AdminFetchOptions<T> {
  /**
   * Path fragment appended to WORKER_URL. MUST start with '/'.
   * Example: '/v2/staging/entities.json'.
   * No query string here — pass via `search` if needed.
   */
  path: string
  /**
   * Label used verbatim in the error message for non-2xx responses:
   *   `${label} fetch failed: ${res.statusText}`
   * MUST match the prior call-site prefix byte-for-byte so downstream
   * consumers see identical text.
   */
  label: string
  /**
   * Predicate over the parsed artifact — returns true when the body
   * is structurally "empty" (no rows). `kind` becomes `'empty'`;
   * otherwise `'ok'`.
   */
  isEmpty: (artifact: T) => boolean
  /**
   * Optional pre-built URLSearchParams. If supplied and non-empty,
   * appended as `?<params>` after the path. Callers construct the
   * params themselves — the helper does not introspect them.
   */
  search?: URLSearchParams
}

/**
 * Admin → Worker read helper. Returns a discriminated union so
 * callers branch on `kind` without try/catch.
 */
export async function doAdminReadFetch<T>(
  opts: AdminFetchOptions<T>,
): Promise<FetchResult<T>> {
  // Read VITE_WORKER_URL at call time (not module load) so Vitest's
  // `vi.stubEnv()` per-test stubs are honored. In production, Vite
  // inlines the value at build time so every call returns the same
  // string; the call-time read is observationally identical to the
  // prior module-scope constant.
  const workerUrl: string = import.meta.env.VITE_WORKER_URL ?? ''
  if (!workerUrl) {
    return { kind: 'error', message: 'VITE_WORKER_URL not configured' }
  }
  const base = workerUrl.replace(/\/$/, '')
  const qs = opts.search ? opts.search.toString() : ''
  const url = qs.length > 0 ? `${base}${opts.path}?${qs}` : `${base}${opts.path}`
  try {
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
    })
    if (!res.ok) {
      return {
        kind: 'error',
        message: `${opts.label} fetch failed: ${res.statusText}`,
        status: res.status,
      }
    }
    const artifact = (await res.json()) as T
    const kind = opts.isEmpty(artifact) ? 'empty' : 'ok'
    return { kind, artifact }
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'unknown network error'
    return { kind: 'error', message }
  }
}
